/**
 * What this branch changed in the model, and what that obliges a re-review of.
 *
 * GitLab issue #30. The counterpart to CommitHistory and deliberately NOT an
 * extension of it: that one links out to GitLab and answers "what landed on the
 * branch", because GitLab is the record of what happened to the files. This
 * answers "which elements moved", which only the projection can say - a commit
 * that moves a requirement between files is a delete and an add to git while
 * being neither to the model.
 *
 * Presentational, the same rule CommitHistory and RequirementGrid follow: given
 * a comparison, it draws it. Which branch to compare, and how deep to walk, is
 * the screen's to know.
 *
 * THREE SECTIONS, IN THIS ORDER, and the order is the argument:
 *
 *   1. Requirements changed - what somebody did.
 *   2. To re-review - what that means for everything else. THE POINT OF THE
 *      ISSUE, so it is not buried under the element list.
 *   3. Elements - the full syntax-level detail, collapsed. It is long, it is
 *      mostly uninteresting, and a reviewer who wants it knows they want it.
 *
 * The staleness banner is above all three and is not dismissible. Neither side
 * of the comparison is a ref - each is BEST's working copy of one - so a target
 * read before somebody else merged into it reports their work as this branch's
 * change. That is a wrong answer that looks exactly like a right one.
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import DifferenceIcon from '@mui/icons-material/Difference'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { ApiError } from '../../api'
import type {
  ChangeStatus,
  ElementChange,
  FieldChange,
  ModelChanges,
  RequirementChange,
  ReviewItem,
  Schema,
} from '../../schema'
import { useSchema } from '../../state/AppData'
import { MONOSPACE } from '../../theme'
import ChangeLink from './ChangeLink'
import EditElementButton from './EditElementButton'
import ElementCommentButton from './ElementCommentButton'
import {
  editTargetOf,
  elementLink,
  requirementPage,
  useElementThreadCounts,
} from './elementThreads'
import type { EditTarget } from './elementThreads'
import EmptyState from './EmptyState'

/** How each status reads, and in which colour. */
const STATUS: Record<ChangeStatus, 'success' | 'error' | 'info'> = {
  added: 'success',
  removed: 'error',
  modified: 'info',
}

/**
 * How a link reads to somebody deciding whether to open it.
 *
 * The direction is the whole content of a source link, so it is spelled out
 * rather than left as the API's one-word key: 'source' and 'derived' are the
 * same edge read from two ends, and getting them the wrong way round inverts
 * the flowdown a reviewer is checking.
 */
const VIA: Record<string, string> = {
  source: 'is a source of',
  derived: 'derives from',
  related: 'is cross-referenced to',
  // Further out, along a path that changed direction, so no single
  // relationship is true of the pair. The path is shown beside it.
  linked: 'is linked to',
}

/**
 * One field's before and after, on one line.
 *
 * Truncated hard. A statement can be a paragraph, and a table cell that grows
 * to hold one pushes every other row off the screen - so the cell says what
 * changed and the full text is a hover away.
 */
function FieldCell({ fields }: { fields: FieldChange[] }) {
  if (!fields.length) return <Typography variant="body2" color="text.disabled">—</Typography>

  return (
    <Stack spacing={0.25}>
      {fields.map(([name, was, now]) => (
        <Tooltip key={name} title={`${was} → ${now}`} describeChild>
          <Typography variant="caption" sx={{ display: 'block' }}>
            <Box component="span" sx={{ fontFamily: MONOSPACE }}>
              {name}
            </Box>
            {': '}
            <Box component="span" sx={{ color: 'text.disabled', textDecoration: 'line-through' }}>
              {clip(was)}
            </Box>{' '}
            <Box component="span">{clip(now)}</Box>
          </Typography>
        </Tooltip>
      ))}
    </Stack>
  )
}

/** As much of a value as fits on a line. */
function clip(text: string, limit = 48): string {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text
}

export interface ChangeListProps {
  changes: ModelChanges | null
  loading: boolean
  error: ApiError | null
  /** What the person is comparing against, for the empty state's wording. */
  target: string
  /**
   * The program, which gives every element row a comment button (issue #99,
   * phase C). Without it the list is as it was: nothing is read and no button
   * is drawn.
   */
  slug?: string
  /**
   * Opens the element's editor (issue #110). Absent, no row has an Edit action.
   * The screen owns the dialog and what happens after a save.
   */
  onEdit?: (target: EditTarget) => void
  /** Why editing is not allowed, from the screen's writer check; empty when it is. */
  editReason?: string
}

export default function ChangeList({
  changes,
  loading,
  error,
  target,
  slug,
  onEdit,
  editReason = '',
}: ChangeListProps) {
  const edit: EditProps = { onEdit, reason: editReason }
  // Collapsed by default and remembered while the screen is open. The element
  // list is the long one and is not what anybody comes here for first.
  const [showElements, setShowElements] = useState(false)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    // A 409 is not a fault: it means one side has never been imported, and the
    // message from the server names the route that fixes it. Said as info so it
    // does not read as BEST having broken.
    return (
      <Alert severity={error.status === 409 ? 'info' : 'error'} sx={{ m: 2 }}>
        <AlertTitle>
          {error.status === 409 ? 'Nothing to compare against yet' : 'Could not compare'}
        </AlertTitle>
        {error.message}
      </Alert>
    )
  }

  if (!changes) return null

  if (!changes.requirements.length && !changes.elements.length) {
    return (
      <Box>
        {changes.stale.map((line) => (
          <Alert key={line} severity="warning" sx={{ m: 2 }}>
            {line}
          </Alert>
        ))}
        <EmptyState
          icon={<DifferenceIcon />}
          title="No model changes on this branch"
          description={`Every element matches ${target}. Saving a change to the model is what puts one here — commits that only move text between files do not, because the model is the same either way.`}
        />
      </Box>
    )
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {changes.stale.map((line) => (
        <Alert key={line} severity="warning">
          {line}
        </Alert>
      ))}

      {changes.requirements.length ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Requirements changed
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requirement</TableCell>
                <TableCell>Level</TableCell>
                <TableCell></TableCell>
                <TableCell>What moved</TableCell>
                {onEdit ? <TableCell></TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.requirements.map((row) => (
                <RequirementRow key={row.identifier} row={row} edit={edit} />
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {changes.review.length ? (
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <FactCheckIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">
              To re-review — {changes.review.length}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Requirements linked to something that changed, out to{' '}
            {changes.depth === 1 ? 'one link' : `${changes.depth} links`}. Nothing
            here changed on this branch — they are listed because what they
            depend on did.
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Requirement</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Depth</TableCell>
                <TableCell>Why</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.review.map((row) => (
                <ReviewRow key={row.identifier} row={row} />
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {changes.elements.length ? (
        <Accordion
          expanded={showElements}
          onChange={() => setShowElements((was) => !was)}
          disableGutters
          variant="outlined"
          // Unmounted while collapsed, not merely hidden. This table is the
          // long one - a branch that touches a package renders a row per
          // element in it - and MUI keeps collapsed children in the DOM by
          // default, so without this the cost of the section is paid by
          // everybody who never opens it.
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Every element that moved — {changes.elements.length}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
              The syntax-level detail behind the lists above, including parts,
              states and everything else that is not a requirement. A member
              changing position among its siblings is not reported here — one
              insert renumbers everything below it, and GitLab's file diff shows
              that better.
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell></TableCell>
                  <TableCell>What moved</TableCell>
                  {slug || onEdit ? <TableCell></TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                <ElementRows rows={changes.elements} slug={slug} edit={edit} />
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  )
}

interface EditProps {
  onEdit?: (target: EditTarget) => void
  reason: string
}

/** One changed requirement. */
function RequirementRow({ row, edit }: { row: RequirementChange; edit: EditProps }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
          {/* A removed requirement is not on this branch: no page to open. */}
          <ChangeLink to={row.status === 'removed' ? null : requirementPage(row.identifier)}>
            {row.identifier}
          </ChangeLink>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.name}
        </Typography>
      </TableCell>
      <TableCell>{row.level === null ? '—' : `L${row.level}`}</TableCell>
      <TableCell>
        <Chip label={row.status} size="small" color={STATUS[row.status]} variant="outlined" />
      </TableCell>
      <TableCell>
        {/* A requirement that now derives from something else has every scalar
            column identical, so this flag is the only thing that can say it
            moved - and it is one of the more review-worthy changes there is. */}
        {row.links_changed ? (
          <Chip label="links" size="small" sx={{ mr: 0.5 }} />
        ) : null}
        <FieldCell fields={row.fields} />
      </TableCell>
      {edit.onEdit ? (
        <TableCell>
          {/* A removed requirement is not on this branch: nothing to edit. */}
          {row.status === 'removed' ? null : (
            <EditElementButton
              target={{ plural: 'requirements', id: row.identifier, label: row.identifier }}
              reason={edit.reason}
              onEdit={edit.onEdit}
            />
          )}
        </TableCell>
      ) : null}
    </TableRow>
  )
}

/** One requirement to look at, and why. */
function ReviewRow({ row }: { row: ReviewItem }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
          <ChangeLink to={requirementPage(row.identifier)}>{row.identifier}</ChangeLink>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.name}
        </Typography>
      </TableCell>
      <TableCell>{row.level === null ? '—' : `L${row.level}`}</TableCell>
      <TableCell>{row.depth}</TableCell>
      <TableCell>
        <Typography variant="caption">
          {VIA[row.via] ?? row.via}{' '}
          <Box component="span" sx={{ fontFamily: MONOSPACE }}>
            {row.because}
          </Box>
          {/* The whole path, for anything more than one hop out: two links away
              the reason is not obvious from the two endpoints alone. */}
          {row.path.length > 2 ? (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              {' '}
              ({row.path.join(' → ')})
            </Box>
          ) : null}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

/**
 * The element rows, with a comment button on each when the list knows its program.
 *
 * Its own component so that the one read of the threads is made when the table is
 * opened (it is unmounted while collapsed) and not by everybody who never opens it.
 */
function ElementRows({
  rows,
  slug,
  edit,
}: {
  rows: ElementChange[]
  slug?: string
  edit: EditProps
}) {
  if (!slug) return <LinkedRows rows={rows} edit={edit} />
  return <CommentableRows rows={rows} slug={slug} edit={edit} />
}

/** The Edit action for a row, or nothing when the list was not given one or the kind has no editor. */
function editFor(schema: Schema, row: ElementChange, edit: EditProps): ReactNode {
  const target = edit.onEdit ? editTargetOf(schema, row) : null
  return target && edit.onEdit ? (
    <EditElementButton target={target} reason={edit.reason} onEdit={edit.onEdit} />
  ) : null
}

/** The rows with a link on each name (issue #102) and no comment button. */
function LinkedRows({ rows, edit }: { rows: ElementChange[]; edit: EditProps }) {
  const schema = useSchema()
  return rows.map((row) => (
    <ElementRow
      key={row.key}
      row={row}
      to={elementLink(schema, row)}
      edit={edit.onEdit ? editFor(schema, row, edit) || <span /> : undefined}
    />
  ))
}

function CommentableRows({
  rows,
  slug,
  edit,
}: {
  rows: ElementChange[]
  slug: string
  edit: EditProps
}) {
  const schema = useSchema()
  // One read for the whole list, grouped by element. If it fails the counts are
  // simply absent: the buttons still work and the list is unaffected.
  const { counts } = useElementThreadCounts(slug)
  return rows.map((row) => (
    <ElementRow
      key={row.key}
      row={row}
      to={elementLink(schema, row)}
      comment={<ElementCommentButton row={row} schema={schema} counts={counts} />}
      edit={editFor(schema, row, edit)}
    />
  ))
}

/** One element that moved, at syntax level. */
function ElementRow({
  row,
  to,
  comment,
  edit,
}: {
  row: ElementChange
  /** The element's own page, or null when it has none (see elementLink). */
  to: string | null
  comment?: ReactNode
  /** The Edit action (issue #110); null where the row cannot be edited. */
  edit?: ReactNode
}) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: MONOSPACE, wordBreak: 'break-all' }}>
          <ChangeLink to={to}>{row.qualified_name}</ChangeLink>
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {row.path}
          {row.line ? `:${row.line}` : ''}
          {/* Rendered only when there is one. Nothing is stamped until it is
              minted, so most elements have none - and that is ordinary, not a
              fault to report in every row. */}
          {row.uid ? ` · ${row.uid}` : ''}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{row.keyword || row.kind}</Typography>
      </TableCell>
      <TableCell>
        <Chip label={row.status} size="small" color={STATUS[row.status]} variant="outlined" />
      </TableCell>
      <TableCell>
        <FieldCell fields={row.fields} />
      </TableCell>
      {comment || edit !== undefined ? (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {comment}
          {edit}
        </TableCell>
      ) : null}
    </TableRow>
  )
}
